import {
  AvatarGroup,
  ChipCounterList,
  Column,
  OverflowTooltip,
} from '../../../components';
import StatusChip from '../../../components/Chip/StatusChip';
import execChangeRenderer from '../../../components/cellRenderers/execChangeRenderer';
import execTimeRenderer from '../../../components/cellRenderers/execTimeRenderer';
import modelRunsTotalsRenderer from '../../../components/cellRenderers/modelRunsTotalsRenderer';
import statusFilterRenderer from '../../../components/cellRenderers/statusFilterRenderer';
import { ColumnFilterOption } from '../../../components/Table/types';
import { Operator } from '../../../hooks/useUrlFilters';
import modelRunsTotalsComparer from '../../../utils/comparers/modelRunsTotalsComparer';
import numericComparer from '../../../utils/comparers/numericComparer';
import { capitalize, distinctArray, isInSaaSMode } from '../../../utils/common';
import { getFilterOptionsOverride } from '../../../utils/graphqlFilters';
import { ModelRuns } from '../../../types';

const getItemUniqueFilterOptions = (
  tableData: ModelRuns[],
  fieldPath: keyof NonNullable<ModelRuns['asset_metadata']>,
) => {
  const allItems =
    tableData
      ?.flatMap((item) => item?.asset_metadata?.[fieldPath] || '')
      .filter((val) => val !== '') || [];
  const distinctItems = distinctArray(allItems as string[]);
  return distinctItems.map((item) => ({ value: item, label: item }));
};

const EQUALS_OPERATORS: Operator[] = ['equals', 'notEquals'];
const NO_RUNS_LABEL = 'No runs';

export const getModelRunsTableColumns = (
  tableData: ModelRuns[] | undefined,
  filterOptionsOverride: Record<string, ColumnFilterOption[]> | null = null,
): Array<Column<ModelRuns>> => {
  const isSaaS = isInSaaSMode();

  const maxExecTime = tableData?.map
    ? Math.max(...tableData.map((item) => Number(item.last_exec_time) || 0))
    : 0;

  const baseColumns: Array<Column<ModelRuns>> = [
    {
      id: 'database_name',
      header: 'Database',
      defaultUntoggled: true,
      filterOptionsOverride: getFilterOptionsOverride(
        'database_name',
        filterOptionsOverride,
      ),
    },
    {
      id: 'schema',
      header: 'Schema',
      filterOptionsOverride: getFilterOptionsOverride(
        'schema',
        filterOptionsOverride,
      ),
    },
    {
      id: 'name',
      header: 'Name',
      nonToggleable: true,
      filterDropdownWidth: 350,
      filterOptionsOverride: getFilterOptionsOverride(
        'name',
        filterOptionsOverride,
      ),
    },
    {
      id: 'last_exec_time',
      header: 'Last exec. time',
      filterable: false,
      renderer: (rowData) =>
        execTimeRenderer(rowData.last_exec_time, maxExecTime),
      compareFunc: (a, b) => numericComparer(a, b, 'last_exec_time'),
      nonToggleable: true,
    },
    {
      id: 'median_exec_time',
      header: 'Median exec. time',
      filterable: false,
      renderer: (rowData) => execTimeRenderer(rowData.median_exec_time),
      compareFunc: (a, b) => numericComparer(a, b, 'median_exec_time'),
    },
    {
      id: 'exec_time_change_rate',
      header: 'Ex time change rate',
      filterable: false,
      renderer: execChangeRenderer,
      compareFunc: (a, b) => numericComparer(a, b, 'exec_time_change_rate'),
    },
    {
      id: 'totals',
      header: 'Results',
      renderer: modelRunsTotalsRenderer,
      compareFunc: modelRunsTotalsComparer,
      filterable: false,
      width: 140,
    },
    {
      id: 'status',
      header: 'Last run status',
      renderer: (value) => <StatusChip status={value.status} />,
      filterRenderer: (value) =>
        value ? statusFilterRenderer(value) : NO_RUNS_LABEL,
      emptyValueLabel: NO_RUNS_LABEL,
      filterOptionsOverride: getFilterOptionsOverride(
        'status',
        filterOptionsOverride,
      ),
    },
    {
      id: 'asset_metadata.owners',
      filterOperators: EQUALS_OPERATORS,
      header: 'Asset Owners',
      defaultUntoggled: true,
      emptyValueLabel: 'No asset owners',
      filterOptionsOverride: getFilterOptionsOverride(
        'asset_metadata.owners',
        filterOptionsOverride,
        getItemUniqueFilterOptions(tableData || [], 'owners'),
      ),
      renderer: ({ asset_metadata }) => (
        <AvatarGroup items={asset_metadata?.owners} />
      ),
    },
    {
      id: 'asset_metadata.tags',
      filterOperators: EQUALS_OPERATORS,
      header: 'Asset Tags',
      defaultUntoggled: true,
      emptyValueLabel: 'No asset tags',
      filterOptionsOverride: getFilterOptionsOverride(
        'asset_metadata.tags',
        filterOptionsOverride,
        getItemUniqueFilterOptions(tableData || [], 'tags'),
      ),
      renderer: ({ asset_metadata }) => (
        <ChipCounterList items={asset_metadata?.tags} />
      ),
    },
    {
      id: 'asset_metadata.materialization',
      header: 'Materialization',
      emptyValueLabel: 'No materialization',
      filterOptionsOverride: getFilterOptionsOverride(
        'asset_metadata.materialization',
        filterOptionsOverride,
        getItemUniqueFilterOptions(tableData || [], 'materialization'),
      ),
      renderer: ({ asset_metadata }) => (
        <OverflowTooltip>
          {asset_metadata?.materialization
            ? capitalize(asset_metadata.materialization)
            : '-'}
        </OverflowTooltip>
      ),
    },
  ];

  if (isSaaS) {
    const assetColumns: Array<Column<ModelRuns>> = [
      {
        id: 'asset_metadata.is_critical',
        filterOperators: EQUALS_OPERATORS,
        header: 'Critical Asset',
        defaultUntoggled: true,
        filterOptionsOverride: getFilterOptionsOverride(
          'asset_metadata.is_critical',
          filterOptionsOverride,
        ),
        renderer: ({ asset_metadata }) => (
          <OverflowTooltip>
            {asset_metadata?.is_critical ? 'Yes' : 'No'}
          </OverflowTooltip>
        ),
      },
      {
        id: 'asset_metadata.subscribers',
        filterOperators: EQUALS_OPERATORS,
        header: 'Asset Subscribers',
        defaultUntoggled: true,
        width: 200,
        emptyValueLabel: 'No asset subscribers',
        filterOptionsOverride: getFilterOptionsOverride(
          'asset_metadata.subscribers',
          filterOptionsOverride,
          getItemUniqueFilterOptions(tableData || [], 'subscribers'),
        ),
        renderer: ({ asset_metadata }) => (
          <AvatarGroup items={asset_metadata?.subscribers} />
        ),
      },
      {
        id: 'asset_metadata.path',
        header: 'Asset Path',
        defaultUntoggled: true,
        hideColumnFilter: true,
        filterOperators: ['contains', 'notContains'],
      },
    ];

    return [...baseColumns, ...assetColumns];
  }

  return baseColumns;
};
